/**
 * useAuthorization Hook
 * 역할 및 권한 체크 기능 제공
 */

import { useMemo } from 'react';
import { useCurrentUser } from './useCurrentUser';
import { Permission, UseAuthorizationResult } from '../../types/auth';

/**
 * 역할/권한 체크 훅
 *
 * @returns {UseAuthorizationResult} user, isLoading, hasRole, hasPermission 등
 *
 * @example
 * const { hasRole, hasPermission } = useAuthorization();
 *
 * if (hasRole('super_admin')) {
 *   // super_admin 전용 UI 표시
 * }
 *
 * if (hasPermission('hr.employees.view')) {
 *   // HR 직원 관리 메뉴 표시
 * }
 */
export function useAuthorization(): UseAuthorizationResult {
  const { user, isLoading } = useCurrentUser();

  // 권한 체크 함수들을 메모이제이션
  const authorizationMethods = useMemo(() => {
    /**
     * 특정 역할을 가지고 있는지 확인
     */
    const hasRole = (roleId: string): boolean => {
      if (!user) return false;
      return user.roles.includes(roleId);
    };

    /**
     * 여러 역할 중 하나라도 가지고 있는지 확인
     */
    const hasAnyRole = (roleIds: string[]): boolean => {
      if (!user) return false;
      return roleIds.some((roleId) => user.roles.includes(roleId));
    };

    /**
     * 특정 권한을 가지고 있는지 확인
     * 와일드카드(*) 지원
     *
     * 예:
     * - 사용자가 "users.*" 권한을 가지고 있으면 "users.view", "users.create" 모두 허용
     * - 사용자가 "*" 권한을 가지고 있으면 모든 권한 허용
     */
    const hasPermission = (requiredPermission: Permission): boolean => {
      if (!user) return false;

      // 정확히 일치하는 권한이 있는지 확인
      if (user.permissions.includes(requiredPermission)) {
        return true;
      }

      // 와일드카드 권한 확인
      const parts = requiredPermission.split('.');
      for (let i = parts.length; i > 0; i--) {
        const wildcardPerm = parts.slice(0, i).join('.') + '.*';
        if (user.permissions.includes(wildcardPerm)) {
          return true;
        }
      }

      // 전체 권한 확인
      if (user.permissions.includes('*')) {
        return true;
      }

      return false;
    };

    /**
     * 여러 권한 중 하나라도 가지고 있는지 확인
     */
    const hasAnyPermission = (permissions: Permission[]): boolean => {
      if (!user) return false;
      return permissions.some((permission) => hasPermission(permission));
    };

    /**
     * 모든 권한을 가지고 있는지 확인
     */
    const hasAllPermissions = (permissions: Permission[]): boolean => {
      if (!user) return false;
      return permissions.every((permission) => hasPermission(permission));
    };

    return {
      hasRole,
      hasAnyRole,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
    };
  }, [user]);

  return {
    user,
    isLoading,
    ...authorizationMethods,
  };
}
